/**
 * Hilfsfunktionen zur Konvertierung zwischen verschiedenen Zeitformaten
 * 
 * ProfileCard Format: { Mo: [15, 16, 17], Di: [18, 19] }
 * TimeSelector Format: { 'Mo-afternoon': true, 'Di-after': true }
 */

// Zeitbloecke des Wochenrasters - EINZIGE Quelle fuer Uhrzeiten und Beschriftungen
// im Frontend (TimeSelector, AvailabilityOverlap, Profilkarten lesen von hier).
// `hours` = volle Stunden, die der Block abdeckt; Ende = letzte Stunde + 1.
// Backend-Gegenstueck: backend/src/utils/timeBlocks.js (synchron halten!).
const TIME_BLOCKS = {
  early: { hours: [8, 9], label: '1./2. Stunde', short: '1./2.' },
  mid: { hours: [10, 11], label: '3./4. Stunde', short: '3./4.' },
  late: { hours: [12, 13], label: '5./6. Stunde', short: '5./6.' },
  afternoon: { hours: [14, 15], label: '7./8. Stunde', short: '7./8.' },
  after: { hours: [16, 17], label: 'Nach der Schule', short: 'Nachm.' }
};

/**
 * Uhrzeit-Spanne eines Blocks als Text, z. B. "8-10 Uhr".
 * @param {string} blockId
 * @returns {string} leer fuer unbekannte Bloecke
 */
export const blockHoursLabel = (blockId) => {
  const block = TIME_BLOCKS[blockId];
  if (!block) return '';
  const first = block.hours[0];
  const last = block.hours[block.hours.length - 1];
  return `${first}-${last + 1} Uhr`;
};

/**
 * Langer Blocktitel mit Uhrzeit, z. B. "1./2. Stunde (8-10 Uhr)".
 */
export const blockLabel = (blockId) => {
  const block = TIME_BLOCKS[blockId];
  if (!block) return '';
  return `${block.label} (${blockHoursLabel(blockId)})`;
};

// Stunden-zu-Zeitblock Mapping
const HOUR_TO_BLOCK = {};
Object.keys(TIME_BLOCKS).forEach(blockId => {
  TIME_BLOCKS[blockId].hours.forEach(hour => {
    if (!HOUR_TO_BLOCK[hour]) {
      HOUR_TO_BLOCK[hour] = [];
    }
    HOUR_TO_BLOCK[hour].push(blockId);
  });
});

// Zum LESEN alter Stundenlisten: "Nach der Schule" hiess bis Sep 2026 18-22 Uhr.
// Alte Eintraege mit 18-21 sollen weiter als "Nach der Schule" gelten.
const LEGACY_HOUR_TO_BLOCK = { ...HOUR_TO_BLOCK };
[18, 19, 20, 21].forEach(hour => {
  LEGACY_HOUR_TO_BLOCK[hour] = [...(LEGACY_HOUR_TO_BLOCK[hour] || []), 'after'];
});

/**
 * Konvertiert von ProfileCard Format zu TimeSelector Format
 * @param {Object} availability - { Mo: [15, 16, 17], Di: [18, 19] }
 * @returns {Object} - { 'Mo-afternoon': true, 'Di-after': true }
 */
export const availabilityToTimeSelector = (availability) => {
  if (!availability || typeof availability !== 'object') {
    return {};
  }
  
  const result = {};
  
  Object.keys(availability).forEach(day => {
    const hours = availability[day];
    if (!Array.isArray(hours)) return;
    
    // Finde alle betroffenen Zeitblöcke für diesen Tag (tolerant fuer alte Stundenlisten)
    const blocksSet = new Set();
    hours.forEach(hour => {
      const blocks = LEGACY_HOUR_TO_BLOCK[hour];
      if (blocks) {
        blocks.forEach(block => blocksSet.add(block));
      }
    });
    
    // Setze die Zeitblöcke als aktiv
    blocksSet.forEach(block => {
      result[`${day}-${block}`] = true;
    });
  });
  
  return result;
};

/**
 * Konvertiert von TimeSelector Format zu ProfileCard Format
 * @param {Object} selectedTimes - { 'Mo-afternoon': true, 'Di-after': true }
 * @returns {Object} - { Mo: [15, 16, 17], Di: [18, 19] }
 */
export const timeSelectorToAvailability = (selectedTimes) => {
  if (!selectedTimes || typeof selectedTimes !== 'object') {
    return {};
  }
  
  const result = {};
  
  Object.keys(selectedTimes).forEach(timeKey => {
    if (!selectedTimes[timeKey]) return; // Nur aktive Zeiten
    
    const [day, blockId] = timeKey.split('-');
    if (!day || !blockId || !TIME_BLOCKS[blockId]) return;
    
    if (!result[day]) {
      result[day] = [];
    }
    
    // Füge alle Stunden des Zeitblocks hinzu
    TIME_BLOCKS[blockId].hours.forEach(hour => {
      if (!result[day].includes(hour)) {
        result[day].push(hour);
      }
    });
  });
  
  // Sortiere die Stunden für jeden Tag
  Object.keys(result).forEach(day => {
    result[day].sort((a, b) => a - b);
  });
  
  return result;
};

/**
 * Prüft ob mindestens eine Stunde eines Zeitblocks verfügbar ist
 * UPDATED: Unterstützt sowohl OLD ({Mo: [15,16,17]}) als auch NEW ({Mo-afternoon: true}) Format
 * @param {Object} availability - ProfileCard Format oder TimeSelector Format
 * @param {string} day - Wochentag
 * @param {Array} hours - Array von Stunden
 * @returns {boolean}
 */
export const isTimeSlotActive = (availability, day, hours) => {
  if (!availability || !Array.isArray(hours)) return false;
  
  // NEW FORMAT: TimeSelector format {Mo-early: true, Di-mid: false, ...}
  if (typeof availability === 'object' && !Array.isArray(availability)) {
    // Check if any TimeSelector key matches this day and time block
    const timeBlockKeys = Object.keys(availability).filter(key => key.startsWith(day + '-'));
    
    if (timeBlockKeys.length > 0) {
      // TimeSelector format detected
      return timeBlockKeys.some(key => {
        if (!availability[key]) return false; // Not active
        
        // Extract block name (e.g., "Mo-early" -> "early")
        const blockName = key.split('-')[1];
        const block = TIME_BLOCKS[blockName];
        
        if (!block) return false;
        
        // Check if any requested hour overlaps with this block
        return hours.some(hour => block.hours.includes(hour));
      });
    }
  }
  
  // OLD FORMAT: ProfileCard format {Mo: [15,16,17], Di: [18,19]}
  if (availability[day] && Array.isArray(availability[day])) {
    return hours.some(hour => availability[day].includes(hour));
  }
  
  return false;
};

// ---------------------------------------------------------------------------
// Helfer fuer die Session-Anlage: Wochenraster -> konkreter Termin
// ---------------------------------------------------------------------------

/** Reihenfolge der Tage im TimeSelector-Raster. JS-getDay(): Mo=1 ... Fr=5. */
export const WEEKDAY_IDS = ['Mo', 'Di', 'Mi', 'Do', 'Fr'];
export const WEEKDAY_LABELS = { Mo: 'Montag', Di: 'Dienstag', Mi: 'Mittwoch', Do: 'Donnerstag', Fr: 'Freitag' };

/** Reihenfolge der Zeitbloecke im Raster (synchron mit TimeSelector). */
export const TIME_BLOCK_ORDER = ['early', 'mid', 'late', 'afternoon', 'after'];

const pad2 = (n) => String(n).padStart(2, '0');

/**
 * Formatiert ein lokales Datum als YYYY-MM-DD (ohne UTC-Sprung wie bei toISOString()).
 */
export const toLocalDateString = (date) => `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

/**
 * Prueft, ob im Raster ueberhaupt ein Zeitblock aktiv ist.
 * Unterstuetzt TimeSelector-Format ({ 'Mo-early': true }) und Legacy ({ Mo: [8, 9] }).
 */
export const hasAnyAvailability = (availability) => {
  if (!availability || typeof availability !== 'object') return false;
  return Object.values(availability).some(value => (
    value === true || (Array.isArray(value) && value.length > 0)
  ));
};

/**
 * Start- und Endzeit eines Zeitblocks als "HH:MM" (Ende = letzte Stunde + 1).
 * @param {string} blockId - z. B. 'mid'
 * @returns {{ startTime: string, endTime: string } | null}
 */
export const blockTimeRange = (blockId) => {
  const block = TIME_BLOCKS[blockId];
  if (!block) return null;
  const first = block.hours[0];
  const last = block.hours[block.hours.length - 1];
  return { startTime: `${pad2(first)}:00`, endTime: `${pad2(last + 1)}:00` };
};

/**
 * Wochentag-Kuerzel (Mo..Fr) fuer ein Datum "YYYY-MM-DD"; null am Wochenende/ungueltig.
 */
export const weekdayIdForDate = (dateStr) => {
  if (!dateStr) return null;
  const date = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  return WEEKDAY_IDS[date.getDay() - 1] || null;
};

/**
 * Zeitblock, in den eine Uhrzeit "HH:MM" faellt; null ausserhalb des Rasters.
 */
export const blockForTime = (timeStr) => {
  if (!timeStr) return null;
  const hour = parseInt(String(timeStr).split(':')[0], 10);
  if (Number.isNaN(hour)) return null;
  const blocks = HOUR_TO_BLOCK[hour];
  return blocks && blocks.length > 0 ? blocks[0] : null;
};

/**
 * Naechstes Datum (YYYY-MM-DD) mit dem gewuenschten Wochentag, ab "now".
 * Heute zaehlt, wenn der Zeitblock noch nicht begonnen hat.
 * @param {string} dayId - 'Mo' .. 'Fr'
 * @param {string} blockId - Zeitblock, dessen Startstunde die "heute noch?"-Frage entscheidet
 * @param {Date} [now]
 */
export const nextDateForWeekday = (dayId, blockId, now = new Date()) => {
  const targetIndex = WEEKDAY_IDS.indexOf(dayId);
  if (targetIndex === -1) return null;
  const targetDay = targetIndex + 1; // getDay(): Mo=1
  const block = TIME_BLOCKS[blockId];
  const blockStartHour = block ? block.hours[0] : 0;

  const candidate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let diff = (targetDay - candidate.getDay() + 7) % 7;
  if (diff === 0 && now.getHours() >= blockStartHour) {
    diff = 7; // Block heute schon vorbei -> naechste Woche
  }
  candidate.setDate(candidate.getDate() + diff);
  return toLocalDateString(candidate);
};

export { TIME_BLOCKS, HOUR_TO_BLOCK };